// src/pages/RegisterPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register, checkUsername } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { initializeUserEncryption, storeKeyData } from '../services/encryptionService';
import RecoveryKeyModal from '../components/RecoveryKeyModal';
import { PasswordInput } from '../components/ui';

// Cloudflare Turnstile
interface TurnstileInstance {
    render: (element: HTMLElement, options: Record<string, unknown>) => string;
    remove: (widgetId: string) => void;
    reset: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile: TurnstileInstance | undefined;
    }
}

// Icons
const UserIcon = () => (
    <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const EmailIcon = () => (
    <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);

const ShieldIcon = () => (
    <div className="p-2 sm:p-2.5 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg sm:rounded-xl border border-emerald-200 dark:border-emerald-500/20 overflow-hidden">
        <img src="/logo.png" alt="AccountSafe" className="w-12 h-12 sm:w-14 sm:h-14 object-contain" />
    </div>
);

const CheckIcon = () => <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
const XIcon = () => <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;

const RegisterPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');

    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);
    const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
    
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Recovery key modal
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const [recoveryKey, setRecoveryKey] = useState('');

    // Turnstile
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [widgetId, setWidgetId] = useState<string | null>(null);
    const turnstileRef = useRef<HTMLDivElement>(null);

    const { setToken } = useAuth();
    const navigate = useNavigate();

    // Load Cloudflare Turnstile script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    // Render Turnstile widget
    useEffect(() => {
        if (!turnstileRef.current || widgetId) return;

        const renderWidget = () => {
            if (window.turnstile && turnstileRef.current && !widgetId) {
                const id = window.turnstile.render(turnstileRef.current, {
                    sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '',
                    callback: (token: string) => {
                        setTurnstileToken(token);
                    },
                    'error-callback': () => {
                        setTurnstileToken(null);
                    },
                    theme: 'dark',
                    size: 'normal',
                });
                setWidgetId(id);
            }
        };

        if (window.turnstile) {
            renderWidget();
        } else {
            const interval = setInterval(() => {
                if (window.turnstile) {
                    renderWidget();
                    clearInterval(interval);
                }
            }, 100);

            return () => clearInterval(interval);
        }

        return () => {
            if (widgetId && window.turnstile) {
                window.turnstile.remove(widgetId);
            }
        };
    }, [widgetId]);
    
    useEffect(() => {
        if (!username) {
            setIsUsernameAvailable(null);
            return;
        }
        setIsCheckingUsername(true);
        const timerId = setTimeout(() => {
            checkUsername(username).then(data => {
                setIsUsernameAvailable(!data.exists);
                setIsCheckingUsername(false);
            });
        }, 500);
        return () => clearTimeout(timerId);
    }, [username]);

    useEffect(() => {
        const errors: string[] = [];
        if (password.length < 8) errors.push("At least 8 characters");
        if (!/[a-z]/.test(password)) errors.push("At least one lowercase letter");
        if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
        if (!/[0-9]/.test(password)) errors.push("At least one number");
        if (!/[\W_]/.test(password)) errors.push("At least one special character");
        setPasswordErrors(errors);
    }, [password]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!turnstileToken) {
            setError('Please complete the verification challenge.');
            return;
        }

        if (password !== password2) {
            setError('Passwords do not match');
            return;
        }
        if (passwordErrors.length > 0) {
            setError('Please fix the errors in your password.');
            return;
        }
        if (isUsernameAvailable === false) {
            setError('This username is already taken.');
            return;
        }

        setIsLoading(true);
        try {
            // Generate recovery key (for backup purposes)
            const { recoveryKey: generatedRecoveryKey } = await initializeUserEncryption();
            
            // Register account (generates salt internally and returns it)
            const registerResponse = await register(username, email, password, password2, turnstileToken);
            const salt = registerResponse.salt;
            setSuccess('Account created successfully!');
            
            // Store recovery key
            storeKeyData(salt, generatedRecoveryKey);
            
            // Set auth token (already done by register function)
            const authToken = registerResponse.key || registerResponse.token;
            if (authToken) {
                setToken(authToken);
                
                // Show recovery key modal
                setRecoveryKey(generatedRecoveryKey);
                setShowRecoveryModal(true);
            } else {
                setError('Registration succeeded but auto-login failed. Please log in manually.');
            }
        } catch (err: unknown) {
            const axiosError = err as { response?: { data?: { username?: string[]; email?: string[]; password?: string[]; error?: string } }; message?: string };
            if (axiosError.response?.data) {
                const errors = axiosError.response.data;
                const errorMessage = errors.username?.[0] || errors.email?.[0] || errors.password?.[0] || errors.error || 'Registration failed.';
                setError(errorMessage);
            } else {
                setError(axiosError.message || 'An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecoveryKeyConfirm = () => {
        setShowRecoveryModal(false);
        navigate('/', { replace: true });
    };

    const renderUsernameFeedback = () => {
        if (!username) return null;
        if (isCheckingUsername) {
            return (
                <p className="text-xs text-zinc-500 mt-1.5 flex items-center">
                    <svg className="animate-spin h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking availability...
                </p>
            );
        }
        if (isUsernameAvailable === true) return <p className="text-xs text-green-400 mt-1.5 flex items-center"><CheckIcon /><span className="ml-1">Username is available</span></p>;
        if (isUsernameAvailable === false) return <p className="text-xs text-red-400 mt-1.5 flex items-center"><XIcon /><span className="ml-1">Username is already taken</span></p>;
        return null;
    };
    
    const renderPasswordFeedback = () => {
        if (!password) return null;
        const requirements = [
            { text: "8+ characters", valid: password.length >= 8 },
            { text: "Lowercase", valid: /[a-z]/.test(password) },
            { text: "Uppercase", valid: /[A-Z]/.test(password) },
            { text: "Number", valid: /[0-9]/.test(password) },
            { text: "Special char", valid: /[\W_]/.test(password) },
        ];

        return (
            <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                {requirements.map(req => (
                    <span 
                        key={req.text} 
                        className={`inline-flex items-center text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                            req.valid 
                                ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
                                : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                        }`}
                    >
                        {req.valid ? <CheckIcon /> : <XIcon />}
                        <span className="ml-0.5 sm:ml-1 whitespace-nowrap">{req.text}</span>
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen flex items-start sm:items-center justify-center px-4 py-8 sm:py-12 bg-white dark:bg-[#09090b] pt-8 sm:pt-0">
            <div className="w-full max-w-md">
                {/* Logo and Title */}
                <div className="text-center mb-6 sm:mb-8">
                    <div className="flex justify-center mb-4 sm:mb-5">
                        <ShieldIcon />
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">Create an account</h1>
                    <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">Start securing your credentials today</p>
                </div>

                {/* Card */}
                <div className="w-full bg-transparent p-0 md:bg-white md:dark:bg-zinc-900/40 md:backdrop-blur-md md:border md:border-zinc-200 md:dark:border-zinc-800/50 md:rounded-2xl md:shadow-xl md:dark:shadow-2xl md:dark:shadow-black/20 md:p-8">
                    {success && (
                        <div className="as-alert-success mb-6">
                            <CheckIcon />
                            <span className="ml-2">{success}</span>
                        </div>
                    )}
                    
                    {error && (
                        <div className="as-alert-danger mb-6">
                            <XIcon />
                            <span className="ml-2">{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate className="space-y-4">
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">Username</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <UserIcon />
                                </div>
                                <input
                                    type="text"
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="as-input pl-10 h-11 md:h-12"
                                    placeholder="Choose a username"
                                    required
                                />
                            </div>
                            {renderUsernameFeedback()}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">Email</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <EmailIcon />
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="as-input pl-10 h-11 md:h-12"
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">Password</label>
                            <PasswordInput
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-11 md:h-12"
                                placeholder="Create a password"
                                autoComplete="new-password"
                                showLeftIcon
                                required
                            />
                            {renderPasswordFeedback()}
                        </div>

                        <div>
                            <label htmlFor="password2" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">Confirm Password</label>
                            <PasswordInput
                                id="password2"
                                value={password2}
                                onChange={(e) => setPassword2(e.target.value)}
                                className="h-11 md:h-12"
                                placeholder="Confirm your password"
                                autoComplete="new-password"
                                showLeftIcon
                                required
                            />
                            {password2 && password !== password2 && (
                                <p className="text-xs text-red-500 mt-1.5 flex items-center"><XIcon /><span className="ml-1">Passwords do not match</span></p>
                            )}
                        </div>

                        {/* Cloudflare Turnstile */}
                        <div className="flex justify-center py-2">
                            <div ref={turnstileRef} className="transform scale-90 sm:scale-100"></div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={isLoading || !turnstileToken}
                            className="w-full as-btn-primary py-3 mt-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Creating account...</span>
                                </>
                            ) : (
                                <span>Create account</span>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
                        <p className="text-sm text-zinc-400">
                            Already have an account?{' '}
                            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
                
                {/* Footer */}
                <p className="mt-6 text-center text-xs text-zinc-600">
                    Protected by end-to-end encryption
                </p>
            </div>

            {/* Recovery Key Modal */}
            <RecoveryKeyModal
                isOpen={showRecoveryModal}
                recoveryKey={recoveryKey}
                username={username}
                onConfirm={handleRecoveryKeyConfirm}
            />
        </div>
    );
};

export default RegisterPage;
